import type { OnboardingStatus } from '@/models/onboarding-status.model';

import httpClient from "@/api/http-client";
import { OnboardingApplicationStatus } from '@/models/onboarding-status.model';
import {
  type OnboardingVerification,
  OnboardingVerificationItemName,
  OnboardingVerificationItemOutcome,
  OnboardingVerificationItemOutcomeUnset,
  OnboardingVerificationItemStatus,
} from '@/models/onboarding-verification.model';
import { AppPages } from '@/router/pages';
import type { AgreementSigningDocument } from '@/models/agreement-signing-session.model';
import type { OnboardingDocument } from '@/models/onboarding-document.model';

type OnboardingStepVerificationRequirement = {
  name: OnboardingVerificationItemName,
  outcome?: Array<OnboardingVerificationItemOutcome | typeof OnboardingVerificationItemOutcomeUnset>,
  status?: OnboardingVerificationItemStatus[],
}
type OnboardingStepRequirements = {
  applicationStatus?: OnboardingApplicationStatus[],
  verifications: OnboardingStepVerificationRequirement[],
}

const onboardingStepRequirements = {
  [AppPages.OnboardingBusinessDetails]: {
    verifications: [ {
      name: OnboardingVerificationItemName.SignUp,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.Kyb,
      status: [ OnboardingVerificationItemStatus.Pending ],
    } ],
  },
  [AppPages.OnboardingBusinessOwners]: {
    verifications: [ {
      name: OnboardingVerificationItemName.SignUp,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.Kyb,
      status: [ OnboardingVerificationItemStatus.Submitted, OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.IdentityVerification,
      outcome: [ OnboardingVerificationItemOutcome.Rejected,
        OnboardingVerificationItemOutcome.Approved,
        OnboardingVerificationItemOutcome.ManualReview,
        OnboardingVerificationItemOutcomeUnset ],
      status: [ OnboardingVerificationItemStatus.Pending,
        OnboardingVerificationItemStatus.Submitted, 
        OnboardingVerificationItemStatus.Completed,  
       ],
    } ,
    {
      name: OnboardingVerificationItemName.Agreement,
      outcome: [ OnboardingVerificationItemOutcomeUnset ],
      status: [ OnboardingVerificationItemStatus.Pending ],
    } ],
  },
  [AppPages.OnboardingAgreementSigning]: {
    verifications: [ {
      name: OnboardingVerificationItemName.SignUp,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.Kyb,
      outcome: [
        OnboardingVerificationItemOutcomeUnset,
        OnboardingVerificationItemOutcome.Approved,
        OnboardingVerificationItemOutcome.ManualReview,
      ],
      status: [ OnboardingVerificationItemStatus.Submitted, OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.IdentityVerification,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.Agreement,
      status: [ OnboardingVerificationItemStatus.Submitted ],
    } ],
  },
  [AppPages.RedirectToProduct]: {
    verifications: [ {
      name: OnboardingVerificationItemName.SignUp,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.Kyb,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.IdentityVerification,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    } , {
      name: OnboardingVerificationItemName.Agreement,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.LienFiling,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.BankAccount,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.Documents,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    } ],
  },
  [AppPages.OnboardingStatusApproved]: {
      verifications: [ {
      name: OnboardingVerificationItemName.SignUp,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.Kyb,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.IdentityVerification,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    } , {
      name: OnboardingVerificationItemName.Agreement,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    }, {
      name: OnboardingVerificationItemName.LienFiling,
      outcome: [ OnboardingVerificationItemOutcome.Approved ],
      status: [ OnboardingVerificationItemStatus.Completed ],
    } ],
  },
  [AppPages.OnboardingStatusRejected]: {
    applicationStatus: [ OnboardingApplicationStatus.Rejected ],
    verifications: [],
  },
  [AppPages.OnboardingStatusInReview]: {
    applicationStatus: [ OnboardingApplicationStatus.Incomplete,
      OnboardingApplicationStatus.ManualReview,
      OnboardingApplicationStatus.PendingWorkflow ],
    verifications: [],
  },
};

function verifyStepRequirements (definition: OnboardingStepRequirements, onboardingStatus: OnboardingStatus): boolean {
  if (definition.applicationStatus && !definition.applicationStatus.includes(onboardingStatus.applicationStatus)) {
    return false;
  }

  for (const verification of definition.verifications) {
    const onboardingVerification = onboardingStatus.verifications.find(({ name }) => name === verification.name);

    if (!onboardingVerification) {
      return false;
    }
    if (verification.status && !verification.status.includes(onboardingVerification.status)) {
      return false;
    }
    if (verification.outcome && !verification.outcome.includes(onboardingVerification.outcome)) {
      return false;
    }
  }

  return true;
}

function getNextPage (onboardingStatus: OnboardingStatus) {
  for (const [ page, definition ] of Object.entries(onboardingStepRequirements)) {
    const meetsRequirements = verifyStepRequirements(definition, onboardingStatus);
    if (meetsRequirements) {
      return page;
    }
  }
  return null;
}

const getOnboardingStatus = async (): Promise<OnboardingStatus> => {
  const { data } = await httpClient.get<OnboardingStatus>("users/onboarding-status");
  return data;
};

const sendAgreementSigning = async (): Promise<AgreementSigningDocument> => {
  const { data } = await httpClient.post<AgreementSigningDocument>("users/agreement");
  return data;
};

const updateModuleStatus = async (body:OnboardingVerification): Promise<OnboardingVerification> => {
  const { data } = await httpClient.post<OnboardingVerification>("users/module-status",body);
  return data;
};

const uploadDocument = async (body:OnboardingDocument): Promise<OnboardingDocument> => {
  const { data } = await httpClient.post<OnboardingDocument>("/users/documents", body);
  return data;
};

export default {
  getNextPage,
  getOnboardingStatus,
  sendAgreementSigning,
  updateModuleStatus,
  uploadDocument,
};
